import { useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";

type Profile = {
  trader_code: string;
  email: string;
  avatar_url?: string | null;
  language_preference?: "device" | "en" | "fr" | "ja" | "es";
  notification_locale?: "en" | "fr" | "ja" | "es";
};

type UseProfileArgs = {
  enabled: boolean;
};

const parseApiError = async (response: Response, fallback: string) => {
  const errorText = await response.text();
  if (!errorText) return fallback;

  try {
    const parsed = JSON.parse(errorText) as { detail?: unknown; message?: unknown };
    if (typeof parsed.detail === "string") {
      return parsed.detail;
    }
    if (parsed.detail !== undefined) {
      return JSON.stringify(parsed.detail);
    }
    if (typeof parsed.message === "string") {
      return parsed.message;
    }
  } catch {
    // Keep raw response text when payload is not JSON.
  }

  return errorText;
};

export function useProfile({ enabled }: UseProfileArgs) {
  const { isSignedIn: isAuthenticated, user } = useUser();
  const { acquireToken } = useApiAuth();
  const canLoad = enabled && isAuthenticated;

  const query = useQuery({
    queryKey: ["profile", user?.id],
    enabled: canLoad && Boolean(user?.id),
    queryFn: async () => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required to load profile.");
      }

      const response = await fetch(apiUrl("/api/profile"), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const detail = await parseApiError(
          response,
          `Failed to load profile (${response.status}): No error detail returned`,
        );
        throw new Error(`Failed to load profile (${response.status}): ${detail}`);
      }

      const payload: Profile = await response.json();
      return payload;
    },
  });

  const profile = useMemo(() => (canLoad ? query.data ?? null : null), [canLoad, query.data]);

  return {
    profile,
    isLoading: canLoad ? query.isLoading : false,
    isError: canLoad ? query.isError : false,
    reload: query.refetch,
  };
}

export type UseProfileResult = ReturnType<typeof useProfile>;
